<template>
  <div class="h-100">
    <v-chart ref="chart" :options="options" :echart.sync="echart" :ready="chartReady" @change:series="changeSeries" />
  </div>
</template>

<script>
import { format } from 'date-fns';
import { merge } from 'lodash';
import chartable from '@/mixins/chartable';
import bus from '@/utils/bus';

export default {
  mixins: [chartable],
  props: {
    settings: {
      type: Object,
      required: true,
    },
    /*
     * Commented.by.KWShin.2024.03.07.
     * motlet에 넘어오는 데이터는 부모의 부모 컴포넌트에 선언된 motletOptions 이다.
     */
    motlet: {
      type: Object,
      required: true,
    },
    storeName: {
      type: String,
      required: true,
    },
    activeIncrement: Boolean,
  },
  data() {
    return {
      // BEGIN::Added.by.KWShin.2024.03.12.
      lastTimestamp: 0,
      drawCounter: 0,
      // FINISH::Added.by.KWShin.2024.03.12.
      options: {
        xAxis: {
          type: 'category',
          axisLabel: {
            formatter: value => this.xAxisLabelFormatter(value),
            fontWeight: 'normal',
            fontSize: 9,
            color: '#bbbbaa',
          },
        },
        yAxis: {
          nameGap: 7,
          nameTextStyle: {
            fontWeight: 'normal',
            fontSize: 11,
            color: '#bbbbaa',
          },
          minInterval: 1,
          axisLabel: {
            inside: true,
            showMinLabel: false,
            formatter: value => this.yAxisLabelFormatter(value),
            fontWeight: 'normal',
            fontSize: 9,
            color: '#bbbbaa',
          },
        },
        series: [
          {
            type: 'line',
            markLine: {
              symbol: 'none',
              label: { show: false },
              lineStyle: {
                type: 'solid',
                color: '#b55a09',
                width: 1,
              },
              data: [{ xAxis: 0 }],
            },
          },
        ],
        grid: {
          top: '5px',
          left: '18px',
          right: '11px',
          bottom: '16px',
        },
        tooltip: {
          trigger: 'axis',
          // alwaysShowContent: true, // 한 번 뜨면 자동으로 안 사라짐
          // triggerOn: 'mousemove|click', // 마우스 이동/클릭 시 갱신
          hideDelay: 2000, // 마우스 나가도 바로 숨기지 않음
          // enterable: false, // 툴팁 위에 마우스 올려도 안 사라지게
          axisPointer: {
            type: 'line',
            z: 0,
            label: {
              show: true,
              formatter: params => format(parseInt(params.value), 'HH:mm:ss'),
            },
          },
          formatter: params => this.tooltipFormatter(params),
        },
      },
      echart: null,
    };
  },
  watch: {
    'settings.systemId': {
      handler(value, before) {
        if (before) this.drawMarkLine(parseInt(this.options.series[0].markLine.data[0].xAxis));
      },
    },
    /*
     * Removed.by.KWShin.2024.03.07.
     * 이 무지막지한 데이터를 deep 하게 watch 하다니...
     * 각설하고, X축 길이 설정값이 변경됐을 때 차트를 다시 그려야 하니
     * 필요할 때 부모 쪽에서 initializeChartOptions()를 호출해주는 것으로 변경.
     *
     * 헐~ 이걸 막으니까 차트가 안 그려지네....
     */
    settings: {
      deep: true,
      handler() {
        this.initializeChartOptions();
        // this.options = merge({}, this.options, this.settings.chart);
      },
    },
  },
  computed: {
    xAxisLabelFormatter() {
      return typeof this.settings.formatter.xAxisLabel == 'function'
        ? this.settings.formatter.xAxisLabel
        : function(value) {
            // console.log('in ./components/LineChart.vue, in xAxisLabelFormatter(), param value=', value);
            // BEGIN:HH:mm에서 HH:mm:ss로 수정.2022.07.11.KWShin.
            return format(parseInt(value), 'HH:mm:ss');
            // FINISH:HH:mm에서 HH:mm:ss로 수정.2022.07.11.KWShin.
          };
    },
    yAxisLabelFormatter() {
      return typeof this.settings.formatter.yAxisLabel == 'function'
        ? this.settings.formatter.yAxisLabel
        : function(value) {
            return value;
          };
    },
    tooltipFormatter() {
      return typeof this.settings.formatter.tooltip == 'function' ? this.settings.formatter.tooltip : function() {};
    },
    eventCode() {
      return this.motlet.monitor.eventCode || this.motlet.monitor.monitorCode;
    },
  },
  methods: {
    chartReady: (echart, caller) => {
      caller.addEventClickChart();
      // const interval = setInterval(() => {
      //   const xAxisData = caller.settings.chart.xAxis.data;
      //   if (xAxisData.length) {
      //     clearInterval(interval);
      //     caller.drawMarkLine(xAxisData[xAxisData.length - 1]);
      //   }
      // }, 30);
    },
    // add event chart click
    addEventClickChart() {
      const canvas = this.echart.getDom().getElementsByTagName('canvas')[0];
      canvas.addEventListener('click', event => {
        const data = this.echart.getOption().xAxis[0].data;
        const pixels = data.map((value, index) => this.echart.convertToPixel({ xAxisIndex: 0 }, index));
        const closestValue = pixels.reduce(function(prev, curr) {
          return Math.abs(curr - event.offsetX) < Math.abs(prev - event.offsetX) ? curr : prev;
        });
        const index = pixels.findIndex(value => Math.abs(value) === closestValue);
        bus.$emit(`click:chart-${this.storeName}`, data[index]);
        bus.$emit(`set:active-increment-${this.storeName}`, false);
      });
    },
    // 패러미터로 넘어온 timestamp에 가장 가까운 X축 지점에 markLine을 그린다.
    drawMarkLine(timestamp) {
      const data = this.echart.getOption().xAxis[0].data;
      const closest = data.reduce(function(prev, curr) {
        return Math.abs(curr - timestamp) < Math.abs(prev - timestamp) ? curr : prev;
      });
      this.options.series[0].markLine.data[0].xAxis = closest.toString();
      bus.$emit(`change:timestamp-${this.storeName}`, { eventCode: this.eventCode, timestamp: closest });
    },
    initializeChartOptions() {
      this.options.xAxis.data = [];
      this.options.series.forEach(s => (s.data = []));
      // Added.by.KWShin.204.03.07.
      this.options = merge({}, this.options, this.settings.chart);
    },
    getMarkLineXAxis() {
      let markLineXAxis = 0;
      try {
        const {
          series: [
            {
              markLine: {
                data: [{ xAxis }],
              },
            },
          ],
        } = this.echart.getOption();
        markLineXAxis = xAxis;
      } catch (e) {
        markLineXAxis = 0;
      }
      return parseInt(markLineXAxis);
    },
    /**
     * Modified.by.KWShin.2024.03.12.
     * 라인 차트에 포인트가 하나 추가 될 때마다 이 메소드가 3번 호출된다.
     * drawMark가 3번씩 12번(OperatingSystem.vue의 경우)이 그려지고
     * drawMark가 그려질 때마다 change:timestamp 이벤트가 발생하니 그 또한 12번이다.
     * 이게 웬 헛짓거린가 싶다.
     * 그래서, 실시간일 때는 timestamp가 달라질 때만 drawMark를 그리도록 하여,
     * drawMark 호출횟수도, 이벤트 발생 건수도 1번으로 줄인다.
     */
    changeSeries(series) {
      const [{ data }] = series;
      if (!data.length) return;

      if (this.activeIncrement) {
        /*
         * 실시간 데이터 갱신일 경우 마지막의 xAxis 위치에 markline 을 생성한다.
         */
        const timestamp = data[data.length - 1][2];
        /*
         * 동일 X축 포인트 추가 시 마지막 change:series 이벤트 시에만 markLine을 그린다.
         */
        if (timestamp != this.lastTimestamp) {
          this.lastTimestamp = timestamp;
          this.drawMarkLine(timestamp);
        }
      } else {
        /*
         * Modifield.by.KWShin.2024.03.12.
         * 비실시간 조회일 경우 chart 의 범위를 벗어날 경우 첫번째 or 마지막 위치에 markline 을 생성한다.
         * 위와 같은 방식이었으나,
         * 벗어날 경우 차트의 중앙에 markLine을 생성한다.
         * TimeNavigator를 이용하여 차트를 탐색하는 상황이므로, 관심있는 시각이 차트의 중앙에 표시되어
         * 관심있는 시각의 전후 현황을 같이 볼 수 있게 한다.
         * Modified.again.by.KWShin.2024.08.15.
         * 위와 같은 방식었으나
         * 실시간이 아닌 경우 markLine이 무조건 TimeNavigator상의 기준시각에 가장 근접한 X축 지점에 markLine을 그린다.
         */
        // --------------------------------------------------------------------
        // const {
        //   series: [
        //     {
        //       markLine: {
        //         data: [{ xAxis }],
        //       },
        //     },
        //   ],
        // } = this.echart.getOption();
        // const markLineTimestamp = parseInt(xAxis);
        // const first = data[0][2];
        // const last = data[data.length - 1][2];
        // const center = data[Math.ceil(data.length / 2)][2];
        // const datetime = parseInt(this.$route.query.t ?? 0);

        // if (datetime !== 0 && this.getMarkLineXAxis() === 0) {
        //   this.drawMarkLine(datetime);
        // } else {
        //   // if (markLineTimestamp < first) this.drawMarkLine(first);
        //   // if (markLineTimestamp > last) this.drawMarkLine(last);
        //   if (markLineTimestamp <= first || markLineTimestamp >= last) this.drawMarkLine(center);
        // }
        // --------------------------------------------------------------------
        this.drawMarkLine(this.settings.datetime.getTime());
      }
    },
  },
  created() {
    bus.$on(`click:chart-${this.storeName}`, this.drawMarkLine);
  },
  beforeDestroy() {
    bus.$off(`click:chart-${this.storeName}`, this.drawMarkLine);
  },
};
</script>
